import { Link } from 'react-router-dom';
import type { TourEvent } from '@/features/events';
import { TOUR_HUB_PATH } from '@/features/events';
import { Section } from '@/components/layout';
import { ResponsiveImage } from '@/components/media';
import { Reveal } from '@/components/motion';
import { Sparkle } from '@/components/ornaments';
import { CANCUN_COPY } from '../copy';
import styles from './AntesDeReservarCancun.module.css';

const SOCIAL_ID = 'cancun-prueba-social';
const FAQ_ID = 'cancun-faq';

export interface AntesDeReservarCancunProps {
  event: TourEvent;
}

/**
 * S3.bis · PRUEBA SOCIAL · FAQ · VUELTA A LA GIRA.
 *
 * ⚠️ ADICIÓN EXIGIDA POR EL BRIEF (ADR-006 · master §7 · `07_ARQUITECTURA_LANDINGS.md` §5-6).
 * `DEMO_5_CancunVIP.png` no dibuja ninguno de los tres. Un retiro de comité
 * íntimo se reserva con dudas concretas —qué días, dónde, cuántas plazas— y hoy
 * la página no las respondía en ningún sitio.
 *
 * Aquí NO hay upsell: Cancún **es** el techo del embudo. Las otras cuatro
 * ciudades empujan hacia aquí; esta no empuja a ninguna parte.
 *
 * Se sirve con menos elementos y más aire que su equivalente de México, porque
 * es la página premium: la prueba social no lleva marco, el filete es capilar y
 * las preguntas van a una sola columna con medida corta.
 *
 * LA FAQ ES UN ACORDEÓN `<details>/<summary>` NATIVO: teclado y lector de
 * pantalla sin una línea de JavaScript, y con la respuesta plegada la sección
 * cabe de un vistazo en vez de ser un muro de texto — que en la página del aire
 * es la diferencia entre premium y folleto. La marca «Por anunciar» va en el
 * `summary`, no en la respuesta: si viviera dentro del pliegue, lo que el
 * ADR-003 obliga a declarar sólo se vería al abrir.
 */
export function AntesDeReservarCancun({ event }: AntesDeReservarCancunProps) {
  const { social, faq, volver } = CANCUN_COPY;

  return (
    <Section background="surface" spacing="tight" className={styles.section}>
      {/* ---------- Prueba social ---------- */}
      <Reveal className={styles.social} aria-labelledby={SOCIAL_ID}>
        <p className={styles.eyebrow} id={SOCIAL_ID}>
          {social.eyebrow}
        </p>

        <div className={styles.socialMedia}>
          <ResponsiveImage
            asset="comun/charla-miami-2023"
            sizes="(min-width: 64rem) 34vw, 84vw"
            className={styles.socialPhoto}
          />
        </div>

        <p className={styles.socialCaption}>{social.caption}</p>
      </Reveal>

      {/* ---------- FAQ ---------- */}
      <Reveal className={styles.faq} aria-labelledby={FAQ_ID}>
        <p className={styles.eyebrow}>{faq.eyebrow}</p>

        <h2 id={FAQ_ID} className={styles.title}>
          <span className={styles.titleRule} />
          <span>{faq.titulo}</span>
          <span className={styles.titleRule} />
        </h2>

        <ul className={styles.faqList} role="list">
          {event.faq.map((item) => (
            <li key={item.id} className={styles.faqItem}>
              {/* `name` compartido = sólo una respuesta abierta a la vez. Es
               * comportamiento nativo del navegador; donde no esté soportado
               * degrada a acordeón normal, nunca a contenido inaccesible. */}
              <details className={styles.faqDetails} name={FAQ_ID}>
                <summary className={styles.faqSummary}>
                  <span className={styles.faqQuestion}>
                    {item.question}
                    {item.isPending ? <span className={styles.faqPending}>{faq.pendiente}</span> : null}
                  </span>
                  <span className={styles.faqMark} aria-hidden="true" />
                </summary>
                <p className={styles.faqAnswer}>{item.answer}</p>
              </details>
            </li>
          ))}
        </ul>
      </Reveal>

      {/* ---------- Vuelta a la gira ---------- */}
      <p className={styles.back}>
        <Link to={TOUR_HUB_PATH} className={styles.backLink}>
          <Sparkle size={14} aria-hidden="true" />
          <span>
            {volver.label}
            <span className="az-visually-hidden"> (desde {event.city})</span>
          </span>
        </Link>
      </p>
    </Section>
  );
}
